import { useCallback, useEffect, useState } from "react"
import { Platform } from "react-native"
import { Camera } from "expo-camera"
import { getRecordingPermissionsAsync } from "expo-audio"
import Constants from "expo-constants"
import * as Device from "expo-device"
import { permissionState } from "./permissionState"
import {
    MISS,
    accessOf,
    browserKey,
    componentSignatures,
    configSignature,
    deviceInUse,
    nativeBuildKey,
    whichChanged,
    whyNotSkip,
} from "./verifiedMemory"
import { IS_WEB } from "../shared/platform"

/**
 * La vérification silencieuse : une fraction de seconde, aucune question posée.
 *
 * Elle ne lit que ce qui se lit **sans rien ouvrir** : l'état des permissions,
 * et — sur le web — la liste des périphériques branchés. Ni fenêtre
 * d'autorisation, ni flux, ni diode qui s'allume. C'est ce qui permet de la
 * faire à chaque entrée sans que l'élève s'en aperçoive.
 *
 * Ce qu'elle NE peut pas dire : si on t'entend, si tu entends, si on te voit.
 * Ça, seul l'élève peut le confirmer — et c'est précisément la partie qu'on
 * cherche à ne pas lui redemander pour rien.
 *
 * @param {{signature: string, components?: object, at: number}|null} remembered
 * @returns {{ready: boolean, matches: boolean, reason: string|null, changedParts: string[], read: function}}
 *   `matches` : la configuration n'a pas bougé, les étapes que seul l'élève
 *   peut juger n'ont pas besoin d'être rejouées.
 *   `reason` : pourquoi elles le sont quand même — une valeur de `MISS`, ou
 *   `null` quand le raccourci s'applique.
 *   `changedParts` : quand `reason` vaut `MISS.CHANGED`, lequel des trois
 *   morceaux (`browser`/`microphone`/`camera`) diffère — vide si le souvenir
 *   comparé n'a pas de détail (écrit avant ce diagnostic). Jamais un libellé
 *   ou un identifiant, juste de quoi orienter un correctif.
 *   `read` : relit l'empreinte à l'instant, pour les périphériques donnés en
 *   argument. C'est ce qu'il faut mémoriser en fin de parcours — surtout pas
 *   celle du montage, voir plus bas.
 */
export const useRememberedCheck = (remembered) => {
    const [result, setResult] = useState({ ready: false, matches: false, reason: null, changedParts: [] })

    /**
     * L'empreinte de MAINTENANT.
     *
     * Volontairement une fonction, et non la valeur lue au montage. Au montage,
     * l'élève qui découvre l'écran n'a encore rien autorisé : la lecture y dit
     * « non demandé ». Mémoriser cette empreinte-là en fin de parcours
     * enregistrait un état que la visite suivante ne retrouvait jamais —
     * l'accès y est désormais accordé, l'empreinte diffère, et le test
     * revenait à chaque fois. C'est le défaut qui rendait tout le lot
     * inopérant pour qui n'avait pas déjà autorisé son matériel avant.
     */
    const read = useCallback(async (chosen = {}) => {
        try {
            const snapshot = IS_WEB ? await webSnapshot(chosen) : await nativeSnapshot()
            if (snapshot.microphone !== "granted" || snapshot.camera !== "granted") return null
            return { signature: configSignature(snapshot), components: componentSignatures(snapshot) }
        } catch (error) {
            return null
        }
    }, [])

    useEffect(() => {
        let cancelled = false

        /*
         * Le raccourci exige que les DEUX accès soient explicitement accordés.
         * Un navigateur qui ne sait pas répondre retombe donc sur le parcours
         * complet, et c'est le bon défaut : mieux vaut une minute de trop
         * qu'une promesse en l'air. `read` renvoie null dans ce cas.
         */
        // Une empreinte illisible se dit avec une chaîne vide : c'est
        // `whyNotSkip` qui en tire la conséquence, pas cet appelant. Le filet
        // d'erreur y passe aussi — `read` avale déjà les siennes, mais un
        // imprévu ne doit pas laisser l'écran sur son indicateur d'attente.
        const settle = (current) => {
            if (cancelled) return
            const reason = whyNotSkip(remembered, current?.signature ?? "", Date.now())
            const changedParts = reason === MISS.CHANGED ? whichChanged(remembered, current?.components) : []
            setResult({ ready: true, matches: reason === null, reason, changedParts })
        }

        read()
            .then(settle)
            .catch(() => settle(null))

        return () => {
            cancelled = true
        }
        // `remembered` n'est lu qu'au montage : la mémoire ne change pas
        // pendant qu'on est sur l'écran, et la relire ferait clignoter l'écran
        // au moment même où l'on vient de l'écrire.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return { ...result, read }
}

/**
 * @param {{audioinput?: string|null, videoinput?: string|null}} chosen
 *   les périphériques que l'élève a désignés lui-même, s'il l'a fait
 */
async function webSnapshot(chosen = {}) {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
        return { platform: "web", build: "inconnu", microphone: null, camera: null }
    }

    const [microphone, camera] = await Promise.all([
        permissionState("microphone"),
        permissionState("camera"),
    ])

    let audio = []
    let video = []
    try {
        const all = await navigator.mediaDevices.enumerateDevices()
        audio = all.filter((d) => d.kind === "audioinput")
        video = all.filter((d) => d.kind === "videoinput")
    } catch (error) {
        // Liste illisible : on ne sait pas, donc on ne raccourcit pas.
    }

    return {
        platform: "web",
        build: browserKey(navigator.userAgent),
        /*
         * Safari ne répond pas à `permissions.query` pour la caméra ni le
         * micro : il lève une exception plutôt que de renvoyer un état. Sans
         * repli, le raccourci n'y serait jamais possible — or Safari est le
         * navigateur de la moitié des élèves sur téléphone.
         *
         * Le repli est le nom des périphériques. Un navigateur ne révèle le
         * libellé d'une caméra qu'à qui a déjà obtenu l'accès : voir « FaceTime
         * HD Camera » plutôt qu'une chaîne vide EST la preuve que la permission
         * est accordée. C'est silencieux — `enumerateDevices` n'ouvre rien — et
         * c'est le même fait, lu autrement.
         */
        microphone: accessOf(microphone, audio),
        camera: accessOf(camera, video),
        audioInput: deviceInUse(audio, chosen.audioinput),
        videoInput: deviceInUse(video, chosen.videoinput),
    }
}

/**
 * Sur natif, `get…PermissionsAsync` lit l'état sans jamais présenter la
 * fenêtre système — c'est exactement ce qu'il faut ici, et c'est déjà ce
 * qu'utilise `useDeviceCheck` quand il ne demande rien.
 */
async function nativeSnapshot() {
    const [cam, mic] = await Promise.all([
        Camera.getCameraPermissionsAsync(),
        getRecordingPermissionsAsync(),
    ])

    return {
        platform: Platform.OS,
        build: nativeBuildKey({
            osVersion: Device.osVersion,
            appVersion: Constants.expoConfig?.version,
        }),
        microphone: mic?.status ?? "inconnu",
        camera: cam?.status ?? "inconnu",
    }
}
